import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { TextInputProps } from './types';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import Input from '../Input/Input';
import {
  applyMask,
  applyMaskWithCursor,
  extractValidCharsOnly,
  MaskConfig,
} from '../utils/maskUtils';

const TextInput: React.FC<TextInputProps> = (props) => {
  const {
    input,
    meta,
    placeholder,
    label,
    textType = 'text',
    readOnly,
    disabled,
    testId,
    size,
    variant = 'underline',
    helperText,
    showClearIcon,
    iconComponent,
    pattern,
    mask,
    includeMask = true,
    value,
    maxLength,
    onChange,
    onBlur,
    onFocus,
    onMouseDown,
    onKeyDown,
    inputRef,
  } = props;
  const { actualTheme } = useContext(ThemeContext);

  const inputValue = useMemo(() => value ?? input.value, [input.value, value]);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const localInputRef: React.Ref<HTMLInputElement> = inputRef || useRef(null);

  const maskConfig = useMemo((): MaskConfig | null => {
    if (!mask) return null;

    if (typeof mask === 'string') {
      return { pattern: mask };
    }

    return mask;
  }, [mask]);

  const [displayValue, setDisplayValue] = useState<string>('');

  useEffect(() => {
    if (!maskConfig) {
      setDisplayValue(inputValue || '');
      return;
    }

    if (!includeMask && inputValue) {
      const visualValue = applyMask(inputValue, maskConfig.pattern);
      setDisplayValue(visualValue);
    } else {
      setDisplayValue(inputValue || '');
    }
  }, [inputValue, maskConfig, includeMask]);

  const handleChange = useCallback(
    (newInputValue: string | number) => {
      const newValue = String(newInputValue);
      let processedValue = newValue;

      if (maskConfig) {
        const inputElement = localInputRef as React.RefObject<HTMLInputElement>;
        const cursorPosition = inputElement.current?.selectionStart || 0;

        const { maskedValue: formattedValue, newCursorPosition } = applyMaskWithCursor(
          newValue,
          maskConfig.pattern,
          cursorPosition,
        );

        processedValue = formattedValue;

        setTimeout(() => {
          if (inputElement.current && typeof newCursorPosition === 'number') {
            inputElement.current.setSelectionRange(newCursorPosition, newCursorPosition);
          }
        }, 0);
      }

      if (pattern && !maskConfig) {
        const patt = new RegExp(pattern);
        const isOneCharShorter = newValue.length === String(inputValue).length - 1;
        const isAlmostEqual = isOneCharShorter && String(inputValue).indexOf(newValue) === 0;
        if (!patt.test(newValue) && !isAlmostEqual) {
          return;
        }
      }

      let formValue = processedValue;
      if (maskConfig && !includeMask) {
        formValue = extractValidCharsOnly(processedValue);
      }

      setDisplayValue(processedValue);

      if (input) input.onChange(formValue);
      if (onChange) onChange(formValue);
    },
    [pattern, inputValue, input, onChange, maskConfig, includeMask, localInputRef],
  );

  const handleClear = useCallback(() => {
    setDisplayValue('');
    if (input) input.onChange('');
    if (onChange) onChange('');
  }, [input, onChange]);

  return (
    <ThemeProvider theme={actualTheme}>
      <Input
        inputRef={localInputRef}
        testId={testId}
        label={label}
        readOnly={readOnly || disabled}
        disabled={disabled}
        value={displayValue}
        type={textType}
        input={input}
        meta={meta}
        autoComplete="off"
        size={size}
        variant={variant}
        helperText={helperText}
        showClearIcon={showClearIcon}
        placeholder={maskConfig?.placeholder || placeholder}
        onChange={handleChange}
        onClear={handleClear}
        rightAddOn={iconComponent}
        onBlur={onBlur}
        onFocus={onFocus}
        onMouseDown={onMouseDown}
        maxLength={maxLength}
        onKeyDown={onKeyDown}
      />
    </ThemeProvider>
  );
};

export default TextInput;
